/**
 * Bank reconciliation — `/finance/bank-reconciliation`.
 *
 * The statement-line worklist plus the split editor. `amount` is signed:
 * positive is money into the account, negative is money out, and the sign IS
 * the direction, so nothing here shows a second direction field that could
 * disagree with it.
 *
 * The split is replaced wholesale, and the parts must add up to the line
 * EXACTLY before it counts as matched (`bank.split_does_not_sum`) — a
 * partially allocated line that says "matched" is a line the reconciliation
 * report counts as done. Every part must also move money the same way the line
 * does (`bank.match_direction_mismatch`). Both rules are checked here in exact
 * decimal, so the editor tells the truth before the request rather than after.
 *
 * Excluding is not deleting. The movement happened, and a statement line that
 * can be made to disappear is a statement nobody can reconcile against — so
 * the action is called exclusion, demands a reason, and is reversible.
 */
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import { Button, Card, CardContent, CardHeader, CardTitle } from "@erp/ui";
import { Plus, RefreshCw, Trash2 } from "lucide-react";

import { DataTable, badgeCell } from "@/components/data-table";
import { surfaceProblem } from "@/components/form";
import { MoneyDisplay } from "@/components/money/money-display";
import { useIdempotentMutation } from "@/features/shared/ops-api";

import {
  BANK_DOCUMENT_TYPES,
  BANK_LINE_STATUSES,
  excludeBankStatementLine,
  postBankStatementLine,
  refreshBankMatchSuggestions,
  replaceBankLineMatches,
  type BankDocumentType,
  type BankLineMatchResult,
  type BankLineMatchWrite,
  type BankStatementLine,
} from "../api";
import {
  AMOUNT_SCALE,
  compareDecimals,
  isNegativeDecimal,
  isZeroDecimal,
  normalizeDecimal,
  subtractDecimals,
  sumDecimals,
} from "../decimal";
import {
  DateField,
  DecimalField,
  DefinitionList,
  NoticeBanner,
  PageHeader,
  ProblemBanner,
  SelectField,
  TextField,
} from "../components/fields";
import {
  financeKeys,
  useBankAccountList,
  useBankMatchSuggestions,
  useBankStatementLine,
  useBankStatementLineList,
  type BankStatementLineFilters,
} from "../queries";

const ALL = "__all__";

const LINE_STATUS_VARIANTS = {
  imported: "outline",
  suggested: "warning",
  matched: "secondary",
  posted: "success",
  excluded: "destructive",
} as const;

interface PartDraft {
  key: string;
  documentType: BankDocumentType;
  documentId: string;
  amount: string;
}

let partSeq = 0;
function newPart(
  documentType: BankDocumentType = "customer_receipt",
  documentId = "",
  amount = "",
): PartDraft {
  partSeq += 1;
  return { key: `part-${partSeq}`, documentType, documentId, amount };
}

export interface BankReconciliationPageProps {
  /** `?statement=` deep link from the statements list. */
  initialStatementFileId?: string;
  canMatch?: boolean;
  canReconcile?: boolean;
  pageLimit?: number;
}

export function BankReconciliationPage({
  initialStatementFileId,
  canMatch = true,
  canReconcile = true,
  pageLimit,
}: BankReconciliationPageProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [bankAccountId, setBankAccountId] = useState<string>(ALL);
  const [status, setStatus] = useState<string>(ALL);
  const [bookedFrom, setBookedFrom] = useState("");
  const [bookedTo, setBookedTo] = useState("");
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);
  const [parts, setParts] = useState<PartDraft[]>([]);
  const [exclusionReason, setExclusionReason] = useState("");
  const [formError, setFormError] = useState<string | null>(null);

  const bankAccountsQuery = useBankAccountList({}, 200);
  const bankAccounts = bankAccountsQuery.rows;

  const filters = useMemo<BankStatementLineFilters>(() => {
    const built: BankStatementLineFilters = {};
    if (bankAccountId !== ALL) built.bank_account_id = bankAccountId;
    if (status !== ALL) built.status = status as BankStatementLine["status"];
    if (initialStatementFileId !== undefined) built.statement_file_id = initialStatementFileId;
    if (bookedFrom !== "") built.booked_from = bookedFrom;
    if (bookedTo !== "") built.booked_to = bookedTo;
    return built;
  }, [bankAccountId, status, initialStatementFileId, bookedFrom, bookedTo]);

  const linesQuery = useBankStatementLineList(filters, pageLimit);
  const lineQuery = useBankStatementLine(selectedLineId);
  const suggestionsQuery = useBankMatchSuggestions(selectedLineId, canMatch);

  const selected = lineQuery.data;

  const onLineChanged = (next: BankLineMatchResult | BankStatementLine) => {
    const line = "statement_line" in next ? next.statement_line : next;
    if ("statement_line" in next) {
      queryClient.setQueryData(financeKeys.bankStatementLines.detail(line.id), next);
    }
    void queryClient.invalidateQueries({ queryKey: financeKeys.bankStatementLines.all });
  };

  const matchMutation = useMutation({
    mutationFn: (input: { statementLineId: string; body: BankLineMatchWrite }) =>
      replaceBankLineMatches(input.statementLineId, input.body),
    onSuccess: (result) => {
      setFormError(null);
      onLineChanged(result);
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const refreshMutation = useIdempotentMutation<unknown, { statementLineId: string }>({
    mutationFn: (input, key) => refreshBankMatchSuggestions(input.statementLineId, key),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: financeKeys.bankStatementLines.suggestions(variables.statementLineId),
      });
      void queryClient.invalidateQueries({ queryKey: financeKeys.bankStatementLines.all });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const excludeMutation = useIdempotentMutation<
    BankStatementLine,
    { statementLineId: string; reason: string }
  >({
    mutationFn: (input, key) =>
      excludeBankStatementLine(input.statementLineId, { reason: input.reason }, key),
    onSuccess: (line) => {
      setExclusionReason("");
      onLineChanged(line);
      void queryClient.invalidateQueries({
        queryKey: financeKeys.bankStatementLines.detail(line.id),
      });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const postMutation = useIdempotentMutation<BankStatementLine, { statementLineId: string }>({
    mutationFn: (input, key) => postBankStatementLine(input.statementLineId, key),
    onSuccess: (line) => {
      onLineChanged(line);
      void queryClient.invalidateQueries({
        queryKey: financeKeys.bankStatementLines.detail(line.id),
      });
    },
    onError: (error) => surfaceProblem(error, t),
  });

  const selectLine = (line: BankStatementLine) => {
    setSelectedLineId(line.id);
    setFormError(null);
    setExclusionReason("");
    setParts([]);
  };

  const partsTotal = useMemo(() => {
    const values: string[] = [];
    for (const part of parts) {
      const value = normalizeDecimal(part.amount, { scale: AMOUNT_SCALE, allowNegative: true });
      if (value !== null) values.push(value);
    }
    return sumDecimals(values, AMOUNT_SCALE);
  }, [parts]);

  const lineAmount = selected?.statement_line.amount.amount ?? "0";
  const splitDifference = subtractDecimals(lineAmount, partsTotal, AMOUNT_SCALE);
  const splitBalances = parts.length > 0 && isZeroDecimal(splitDifference, AMOUNT_SCALE);

  const submitMatch = () => {
    if (selected === undefined) return;
    const lineNegative = isNegativeDecimal(lineAmount, AMOUNT_SCALE);
    const wireParts: { document_type: BankDocumentType; document_id: string; amount: string }[] =
      [];
    for (const [index, part] of parts.entries()) {
      const position = index + 1;
      if (part.documentId.trim() === "") {
        setFormError(
          t("finance.reconciliation.partDocumentRequired", "Part {{part}} needs a document.", {
            part: position,
          }),
        );
        return;
      }
      const amount = normalizeDecimal(part.amount, {
        scale: AMOUNT_SCALE,
        allowNegative: true,
        allowZero: false,
      });
      if (amount === null) {
        setFormError(
          t("finance.reconciliation.partAmountInvalid", "Part {{part}} needs a non-zero amount.", {
            part: position,
          }),
        );
        return;
      }
      if (isNegativeDecimal(amount, AMOUNT_SCALE) !== lineNegative) {
        setFormError(
          t(
            "finance.reconciliation.directionMismatch",
            "Every part must move money the same way the line does.",
          ),
        );
        return;
      }
      wireParts.push({
        document_type: part.documentType,
        document_id: part.documentId.trim(),
        amount,
      });
    }
    if (wireParts.length === 0) {
      setFormError(t("finance.reconciliation.partsRequired", "Add at least one part."));
      return;
    }
    if (compareDecimals(partsTotal, lineAmount, AMOUNT_SCALE) !== 0) {
      setFormError(
        t(
          "finance.reconciliation.splitDoesNotSum",
          "The parts must add up to the line exactly. They are out by {{difference}}.",
          { difference: splitDifference },
        ),
      );
      return;
    }
    setFormError(null);
    matchMutation.mutate({
      statementLineId: selected.statement_line.id,
      body: { parts: wireParts },
    });
  };

  const bankAccountName = (id: string): string => {
    const account = bankAccounts.find((candidate) => candidate.id === id);
    return account === undefined ? id : `${account.code} · ${account.name}`;
  };

  const columns = useMemo<ColumnDef<BankStatementLine>[]>(
    () => [
      {
        accessorKey: "booking_date",
        id: "booking_date",
        header: t("finance.reconciliation.columns.bookingDate", "Booked"),
        enableSorting: false,
        cell: (ctx) => <span dir="ltr">{ctx.getValue<string>()}</span>,
      },
      {
        accessorKey: "counterparty_name",
        id: "counterparty_name",
        header: t("finance.reconciliation.columns.counterparty", "Counterparty"),
        enableSorting: false,
      },
      {
        accessorKey: "raw_reference",
        id: "raw_reference",
        header: t("finance.reconciliation.columns.reference", "Reference"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono text-xs">
            {ctx.getValue<string>()}
          </span>
        ),
      },
      {
        id: "amount",
        header: t("finance.reconciliation.columns.amount", "Amount"),
        enableSorting: false,
        meta: { align: "end" },
        cell: (ctx) => <MoneyDisplay value={ctx.row.original.amount} />,
      },
      {
        id: "unmatched",
        header: t("finance.reconciliation.columns.unmatched", "Unmatched"),
        enableSorting: false,
        meta: { align: "end" },
        cell: (ctx) => <MoneyDisplay value={ctx.row.original.unmatched_amount} />,
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("finance.reconciliation.columns.status", "Status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<string>();
          return badgeCell(
            value,
            LINE_STATUS_VARIANTS,
            t(`finance.reconciliation.statusOption.${value}`, value),
          );
        },
      },
    ],
    [t],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <PageHeader
        title={t("finance.reconciliation.title", "Bank reconciliation")}
        description={t(
          "finance.reconciliation.subtitle",
          "Statement movements, what they settle, and what still has to be explained.",
        )}
      />

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <SelectField
            id="reconciliation-bank-account"
            label={t("finance.reconciliation.filterBankAccount", "Bank account")}
            value={bankAccountId}
            onValueChange={setBankAccountId}
            className="w-72"
            options={[
              { value: ALL, label: t("finance.common.all", "All") },
              ...bankAccounts.map((account) => ({
                value: account.id,
                label: `${account.code} · ${account.name}`,
              })),
            ]}
          />
          <SelectField
            id="reconciliation-status"
            label={t("finance.reconciliation.filterStatus", "Status")}
            value={status}
            onValueChange={setStatus}
            className="w-52"
            options={[
              { value: ALL, label: t("finance.common.all", "All") },
              ...BANK_LINE_STATUSES.map((value) => ({
                value,
                label: t(`finance.reconciliation.statusOption.${value}`, value),
              })),
            ]}
          />
          <DateField
            id="reconciliation-from"
            label={t("finance.reconciliation.filterFrom", "Booked from")}
            value={bookedFrom}
            onChange={setBookedFrom}
            className="w-44"
          />
          <DateField
            id="reconciliation-to"
            label={t("finance.reconciliation.filterTo", "Booked to")}
            value={bookedTo}
            onChange={setBookedTo}
            className="w-44"
          />
        </CardContent>
      </Card>

      <DataTable
        columns={columns}
        query={linesQuery}
        getRowId={(row) => row.id}
        onRowClick={selectLine}
        storageKey="finance.reconciliation.columns"
        emptyMessage={t("finance.reconciliation.empty", "No statement lines match these filters.")}
      />

      {selected === undefined ? null : (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>
                <h2>{t("finance.reconciliation.lineSection", "Statement line")}</h2>
              </CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <DefinitionList
                rows={[
                  {
                    label: t("finance.reconciliation.bankAccount", "Bank account"),
                    value: bankAccountName(selected.statement_line.bank_account_id),
                  },
                  {
                    label: t("finance.reconciliation.bookingDate", "Booked"),
                    value: <span dir="ltr">{selected.statement_line.booking_date}</span>,
                  },
                  {
                    label: t("finance.reconciliation.amount", "Amount"),
                    value: <MoneyDisplay value={selected.statement_line.amount} />,
                  },
                  {
                    label: t("finance.reconciliation.matched", "Matched"),
                    value: <MoneyDisplay value={selected.statement_line.matched_amount} />,
                  },
                  {
                    label: t("finance.reconciliation.unmatched", "Unmatched"),
                    value: <MoneyDisplay value={selected.statement_line.unmatched_amount} />,
                  },
                  {
                    label: t("finance.reconciliation.status", "Status"),
                    value: t(
                      `finance.reconciliation.statusOption.${selected.statement_line.status}`,
                      selected.statement_line.status,
                    ),
                  },
                ]}
              />

              {selected.matches.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {t("finance.reconciliation.noMatches", "Nothing is matched to this line yet.")}
                </p>
              ) : (
                <ul className="flex flex-col divide-y text-sm">
                  {selected.matches.map((match) => (
                    <li key={match.id} className="flex items-center justify-between gap-2 py-2">
                      <span>
                        {t(
                          `finance.reconciliation.documentType.${match.document_type}`,
                          match.document_type,
                        )}
                      </span>
                      <span dir="ltr" className="truncate font-mono text-xs">
                        {match.document_id}
                      </span>
                      <MoneyDisplay value={match.amount} />
                    </li>
                  ))}
                </ul>
              )}

              {selected.statement_line.status === "excluded" ? (
                <NoticeBanner>
                  {t("finance.reconciliation.excludedNotice", "Excluded: {{reason}}", {
                    reason: selected.statement_line.excluded_reason ?? "",
                  })}
                </NoticeBanner>
              ) : null}

              <div className="flex flex-wrap items-end gap-2 border-t pt-4">
                {canMatch && selected.statement_line.status !== "posted" ? (
                  <>
                    <TextField
                      id="exclusion-reason"
                      label={t("finance.reconciliation.exclusionReason", "Exclusion reason")}
                      value={exclusionReason}
                      onChange={setExclusionReason}
                      className="min-w-64 flex-1"
                      maxLength={400}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      loading={excludeMutation.isPending}
                      disabled={exclusionReason.trim() === ""}
                      onClick={() =>
                        excludeMutation.mutate({
                          statementLineId: selected.statement_line.id,
                          reason: exclusionReason.trim(),
                        })
                      }
                    >
                      {t("finance.reconciliation.exclude", "Exclude from the worklist")}
                    </Button>
                  </>
                ) : null}
                {canReconcile ? (
                  <Button
                    type="button"
                    loading={postMutation.isPending}
                    disabled={selected.statement_line.status !== "matched"}
                    onClick={() =>
                      postMutation.mutate({ statementLineId: selected.statement_line.id })
                    }
                  >
                    {t("finance.reconciliation.post", "Post to ledger")}
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex-row items-center justify-between gap-3">
              <CardTitle>
                <h2>{t("finance.reconciliation.matchSection", "Match and split")}</h2>
              </CardTitle>
              {canMatch ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  loading={refreshMutation.isPending}
                  onClick={() =>
                    refreshMutation.mutate({ statementLineId: selected.statement_line.id })
                  }
                >
                  <RefreshCw className="size-4" aria-hidden="true" />
                  {t("finance.reconciliation.refreshSuggestions", "Re-run the match engine")}
                </Button>
              ) : null}
            </CardHeader>
            <CardContent className="flex flex-col gap-4">
              <div>
                <h3 className="text-sm font-medium">
                  {t("finance.reconciliation.suggestions", "Candidates")}
                </h3>
                <p className="text-xs text-muted-foreground">
                  {t(
                    "finance.reconciliation.suggestionsHint",
                    "Strongest evidence first. A suggestion is never a decision — nothing posts until a match is accepted.",
                  )}
                </p>
                {(suggestionsQuery.data ?? []).length === 0 ? (
                  <p className="pt-2 text-sm text-muted-foreground">
                    {t("finance.reconciliation.noSuggestions", "No candidates.")}
                  </p>
                ) : (
                  <ul className="flex flex-col divide-y pt-2 text-sm">
                    {(suggestionsQuery.data ?? []).map((suggestion) => (
                      <li
                        key={suggestion.id}
                        className="flex flex-wrap items-center justify-between gap-2 py-2"
                      >
                        <span className="min-w-0">
                          <span className="font-medium">
                            {t(
                              `finance.reconciliation.documentType.${suggestion.document_type}`,
                              suggestion.document_type,
                            )}
                          </span>{" "}
                          <span className="text-muted-foreground">
                            {t("finance.reconciliation.pass", "pass {{pass}}", {
                              pass: suggestion.pass,
                            })}{" "}
                            · {suggestion.reason}
                          </span>
                        </span>
                        <MoneyDisplay value={suggestion.amount} />
                        {canMatch ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() =>
                              setParts((current) => [
                                ...current,
                                newPart(
                                  suggestion.document_type,
                                  suggestion.document_id,
                                  suggestion.amount.amount,
                                ),
                              ])
                            }
                          >
                            {t("finance.reconciliation.useSuggestion", "Add to split")}
                          </Button>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {canMatch ? (
                <div className="flex flex-col gap-3 border-t pt-4">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-medium">
                      {t("finance.reconciliation.split", "Split")}
                    </h3>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setParts((current) => [...current, newPart()])}
                    >
                      <Plus className="size-4" aria-hidden="true" />
                      {t("finance.reconciliation.addPart", "Add part")}
                    </Button>
                  </div>

                  {parts.map((part, index) => (
                    <div
                      key={part.key}
                      className="grid grid-cols-1 items-end gap-3 border-b pb-3 sm:grid-cols-4"
                    >
                      <SelectField
                        id={`part-type-${part.key}`}
                        label={t("finance.reconciliation.partType", "Document type")}
                        value={part.documentType}
                        onValueChange={(value) =>
                          setParts((current) =>
                            current.map((candidate) =>
                              candidate.key === part.key
                                ? { ...candidate, documentType: value as BankDocumentType }
                                : candidate,
                            ),
                          )
                        }
                        options={BANK_DOCUMENT_TYPES.map((value) => ({
                          value,
                          label: t(`finance.reconciliation.documentType.${value}`, value),
                        }))}
                      />
                      <TextField
                        id={`part-id-${part.key}`}
                        label={t("finance.reconciliation.partDocument", "Document")}
                        value={part.documentId}
                        onChange={(documentId) =>
                          setParts((current) =>
                            current.map((candidate) =>
                              candidate.key === part.key ? { ...candidate, documentId } : candidate,
                            ),
                          )
                        }
                        ltr
                      />
                      <DecimalField
                        id={`part-amount-${part.key}`}
                        label={t("finance.reconciliation.partAmount", "Amount")}
                        value={part.amount}
                        onChange={(amount) =>
                          setParts((current) =>
                            current.map((candidate) =>
                              candidate.key === part.key ? { ...candidate, amount } : candidate,
                            ),
                          )
                        }
                        decimal={{ scale: AMOUNT_SCALE, allowNegative: true, allowZero: false }}
                        invalidMessage={t(
                          "finance.reconciliation.partAmountInvalid",
                          "Part {{part}} needs a non-zero amount.",
                          { part: index + 1 },
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t("finance.reconciliation.removePart", "Remove part {{part}}", {
                          part: index + 1,
                        })}
                        onClick={() =>
                          setParts((current) =>
                            current.filter((candidate) => candidate.key !== part.key),
                          )
                        }
                      >
                        <Trash2 className="size-4" aria-hidden="true" />
                      </Button>
                    </div>
                  ))}

                  <p className="text-sm" data-testid="split-difference">
                    {splitBalances
                      ? t(
                          "finance.reconciliation.splitBalances",
                          "The split adds up to the line exactly.",
                        )
                      : t("finance.reconciliation.splitOutBy", "Out by {{difference}}", {
                          difference: splitDifference,
                        })}
                  </p>

                  {formError === null ? null : <ProblemBanner>{formError}</ProblemBanner>}

                  <div>
                    <Button
                      type="button"
                      loading={matchMutation.isPending}
                      disabled={selected.statement_line.status === "posted"}
                      onClick={submitMatch}
                    >
                      {t("finance.reconciliation.saveSplit", "Replace the split")}
                    </Button>
                  </div>
                </div>
              ) : null}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
